import { useState } from "react";
import { usePageTitle } from "../hooks/usePageTitle";
import Button from "../components/ui/Button";
import Badge from "../components/ui/Badge";
import ScreenshotFrame from "../components/ui/ScreenshotFrame";
import PillarInteractiveDemo from "../components/PillarInteractiveDemo";
import InteractiveVisualizer from "../components/InteractiveVisualizer";
import ProductGrid from "../components/ProductGrid";
import rtgPlannerShot from "../assets/screenshots/website-rtg-planner.jpeg";
import dashboardShot from "../assets/screenshots/website-dashboard.jpeg";

const PILLARS = [
  {
    id: "pm",
    title: "PM Manager",
    subtitle: "The Delivery Workbook",
    icon: "ti-briefcase",
    color: "var(--lilac-deep)",
    tagline: "Stop relitigating the same decision every Tuesday.",
    features: [
      {
        name: "Interactive RACI Matrix",
        desc: "Single source of truth for decision ownership. Roles and approvals are explicitly anchored so scope creep doesn't re-open closed calls.",
      },
      {
        name: "Risk & Issue Register",
        desc: "Structured risk scoring with direct mitigation tracking and owner accountability.",
      },
      {
        name: "Decisions & Change Request Log",
        desc: "Every governance decision and change request recorded with its decision-maker and approval status, so \"when did we agree to that\" always has a real answer.",
      },
      {
        name: "Milestones with Baseline Tracking",
        desc: "Forecast dates tracked against the original baseline, side by side, so slippage shows up early instead of at the steering committee.",
      },
      {
        name: "Tier-Scaled Project Charter",
        desc: "A tier-aligned Charter that pre-fills from data already captured on the initiative and scales its own fields to the project's tier: a light four-field version at Tier 3, the full set plus a risk summary at Tier 1.",
      },
    ],
  },
  {
    id: "cm",
    title: "CM Manager",
    subtitle: "Change Readiness, Scored",
    icon: "ti-users",
    color: "#8B5CF6",
    tagline: "Bridge the gap between workshop energy and Tuesday standup reality.",
    features: [
      {
        name: "Quantitative Readiness Scoring",
        desc: "Scored readiness rubrics and team-response capture, in place of vague 'feeling good' status reports.",
      },
      {
        name: "Stakeholder Influence and Impact Mapping",
        desc: "See who to manage closely, keep satisfied, keep informed or monitor.",
      },
      {
        name: "Adoption Tracking",
        desc: "A scored adoption checklist, so you know the change has landed, not just gone live.",
      },
      {
        name: "CRISP Change-Tier Assessment",
        desc: "The same five-dimension model used for delivery risk (Consequence, Risk, Impact, Scope, Pace) run again through a people lens, so the change tier is read on its own, not assumed from the technical scope.",
      },
    ],
  },
  {
    id: "bi",
    title: "BI Manager",
    subtitle: "DMAIC, Right-Sized",
    icon: "ti-chart-bar",
    color: "#10B981",
    tagline: "Continuous improvement without the Lean Six Sigma bureaucracy.",
    features: [
      {
        name: "Right-Sized DMAIC Process Flows",
        desc: "Define, Measure, Analyse, Improve, Control workflows streamlined specifically for delivery teams.",
      },
      {
        name: "Root Cause & 80/20 Prioritisation",
        desc: "5 Whys, Fishbone and 80/20 prioritisation guides, with root causes captured in BI Manager.",
      },
      {
        name: "Control Plan",
        desc: "A monitoring plan and handover for each improvement, so the gains hold after the project ends.",
      },
    ],
  },
];

// The parts of the platform that don't belong to any one of the three pillars -
// these are the actual differentiators, not the RACI/DMAIC framing every generic
// PM tool can also claim.
const DIFFERENTIATORS = [
  {
    icon: "ti-gauge",
    name: "CRISP Complexity Assessment",
    desc: "Five dimensions, Consequence, Risk, Impact, Scope, Pace, scored twice: once for delivery risk, once for change impact. The two tiers are read independently, so a technically simple initiative that lands hard on people doesn't get waved through on a low score.",
  },
  {
    icon: "ti-books",
    name: "Quick Learning Library",
    desc: "Quick Learning Guides across the lifecycle, plus workshop facilitation packs as they're released (DMAIC Define is available now), built into the workspace next to the work itself, not a PDF pack nobody opens again.",
  },
  {
    icon: "ti-lifebuoy",
    name: "Return-to-Green Planner",
    desc: "A structured recovery plan for initiatives that have slipped: root cause, corrective actions, and a defined path back to green, not just a red status with no next step attached.",
  },
  {
    icon: "ti-sparkles",
    name: "AI Coach",
    desc: "Grounded in Groundwork's methodology and real delivery scenarios. It prompts and supports where you need it, for the judgment call that comes up at 9pm, not a lecture or an assumption about what you already know.",
  },
];

const COMPARISON = [
  {
    feature: "Core Focus",
    groundwork: "Holding delivery decisions & practitioner judgment",
    traditional: "Tasks tracked; decisions left in email and meetings",
  },
  {
    feature: "Decision Relitigation Protection",
    groundwork: "RACI plus a decision log: who decided, when, and whether it was approved",
    traditional: "Decisions buried in comment threads and inboxes",
  },
  {
    feature: "Change Readiness Scoring",
    groundwork: "Scored readiness rubrics & stakeholder influence mapping",
    traditional: "Readiness judged by feel",
  },
  {
    feature: "Business Improvement (DMAIC)",
    groundwork: "Right-sized DMAIC workflows built into delivery",
    traditional: "Improvement run as a separate exercise",
  },
  {
    feature: "Practitioner Setup Time",
    groundwork: "Instant pre-scaffolding out of the box",
    traditional: "Every project set up from a blank page",
  },
];

export default function ProductOverview() {
  usePageTitle("Product Overview");
  const [activeTab, setActiveTab] = useState("pm");
  const pillar = PILLARS.find((p) => p.id === activeTab);

  return (
    <div style={{ maxWidth: 1040, margin: "0 auto", padding: "3rem 24px 5rem" }}>
      {/* Hero Header */}
      <section style={{ textAlign: "center", marginBottom: "3.5rem" }}>
        <div style={{ marginBottom: 16 }}>
          <Badge>Product Deep-Dive</Badge>
        </div>
        <h1
          style={{
            fontFamily: "var(--font-voice)",
            fontSize: "clamp(30px, 4.5vw, 44px)",
            color: "var(--navy)",
            margin: "0 0 16px",
            lineHeight: 1.2,
          }}
        >
          Three Pillars. One Practitioner Platform.
        </h1>
        <p
          style={{
            fontSize: 16,
            color: "var(--text-secondary)",
            maxWidth: 620,
            margin: "0 auto 28px",
            lineHeight: 1.6,
          }}
        >
          Groundwork TierUp bridges Project Management, Change Management, and Business Improvement into a single, cohesive delivery system.
        </p>

        {/* Tab Buttons */}
        <div
          style={{
            display: "inline-flex",
            gap: 8,
            background: "var(--surface-1)",
            padding: 6,
            borderRadius: "var(--radius-lg)",
            border: "1px solid var(--border)",
          }}
        >
          {PILLARS.map((p) => {
            const active = p.id === activeTab;
            return (
              <button
                key={p.id}
                onClick={() => setActiveTab(p.id)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "10px 20px",
                  borderRadius: "var(--radius)",
                  border: "none",
                  background: active ? "var(--surface-0)" : "transparent",
                  color: active ? "var(--navy)" : "var(--text-secondary)",
                  fontWeight: active ? 600 : 500,
                  fontSize: 14,
                  boxShadow: active ? "var(--shadow-sm)" : "none",
                  transition: "all var(--transition-fast)",
                }}
              >
                <i className={`ti ${p.icon}`} style={{ fontSize: 18, color: p.color }} aria-hidden="true" />
                {p.title}
              </button>
            );
          })}
        </div>
      </section>

      {/* Active Pillar Details Card */}
      <section
        className="glass-card"
        style={{
          padding: "2.5rem",
          marginBottom: "4rem",
          background: "linear-gradient(135deg, var(--surface-0) 0%, var(--surface-1) 100%)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 12 }}>
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: "var(--radius)",
              background: "var(--lilac-wash)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <i className={`ti ${pillar.icon}`} style={{ fontSize: 24, color: pillar.color }} aria-hidden="true" />
          </div>
          <div>
            <h2 style={{ fontFamily: "var(--font-voice)", fontSize: 24, color: "var(--navy)", margin: 0 }}>
              {pillar.title}: <span style={{ fontWeight: 400, color: "var(--text-secondary)" }}>{pillar.subtitle}</span>
            </h2>
            <p style={{ fontSize: 14, color: "var(--lilac-deep)", margin: "4px 0 0", fontWeight: 500 }}>
              {pillar.tagline}
            </p>
          </div>
        </div>

        {/* Pillar Feature Cards Grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: 20,
            margin: "2rem 0",
          }}
        >
          {pillar.features.map((f) => (
            <div
              key={f.name}
              style={{
                background: "var(--surface-0)",
                border: "1px solid var(--border)",
                borderRadius: "var(--radius)",
                padding: "1.25rem",
              }}
            >
              <h3 style={{ fontSize: 15, fontWeight: 600, color: "var(--navy)", margin: "0 0 8px" }}>
                {f.name}
              </h3>
              <p style={{ fontSize: 13, color: "var(--text-secondary)", margin: 0, lineHeight: 1.6 }}>
                {f.desc}
              </p>
            </div>
          ))}
        </div>

        {/* Interactive mini-demo of this pillar, tab-matched */}
        <ScreenshotFrame label={pillar.title}>
          <PillarInteractiveDemo pillarId={pillar.id} />
        </ScreenshotFrame>
      </section>

      {/* Moved from the homepage (Stage 1): the tabbed PM / CM / BI demo and the three product cards. */}
      <InteractiveVisualizer />
      <ProductGrid />

      {/* Beyond the three pillars: RTG Planner + Dashboard */}
      <section style={{ marginBottom: "4rem" }}>
        <div style={{ textAlign: "center", marginBottom: "2.5rem" }}>
          <h2 style={{ fontFamily: "var(--font-voice)", fontSize: 28, color: "var(--navy)", margin: "0 0 12px" }}>
            When an initiative needs to escalate, or you need the whole picture
          </h2>
          <p style={{ fontSize: 15, color: "var(--text-secondary)", maxWidth: 560, margin: "0 auto" }}>
            Two more views, working from the same data as the three pillars above.
          </p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))", gap: 24 }}>
          <div>
            <ScreenshotFrame
              src={rtgPlannerShot}
              alt="RTG Planner: the Return to Green register, a governance layer above PM and CM for initiatives that have slipped"
              label="RTG Planner"
            />
            <p style={{ fontSize: 13, color: "var(--text-secondary)", margin: "12px 4px 0", lineHeight: 1.6 }}>
              <strong style={{ color: "var(--navy)" }}>RTG Planner</strong>: a structured recovery plan for initiatives that have slipped, raised as a SteerCo decision and closed only once every criterion is met.
            </p>
          </div>
          <div>
            <ScreenshotFrame
              src={dashboardShot}
              alt="Dashboard: Governance & Execution Control, showing all active projects, changes, and improvements with health and progress at a glance"
              label="Dashboard"
            />
            <p style={{ fontSize: 13, color: "var(--text-secondary)", margin: "12px 4px 0", lineHeight: 1.6 }}>
              <strong style={{ color: "var(--navy)" }}>Dashboard</strong>: every project, change, and improvement you're running, with health and progress rolled up in one place.
            </p>
          </div>
        </div>
      </section>

      {/* Differentiators Section */}
      <section style={{ marginBottom: "4rem" }}>
        <div style={{ textAlign: "center", marginBottom: "2.5rem" }}>
          <h2 style={{ fontFamily: "var(--font-voice)", fontSize: 28, color: "var(--navy)", margin: "0 0 12px" }}>
            Beyond the Three Pillars
          </h2>
          <p style={{ fontSize: 15, color: "var(--text-secondary)", maxWidth: 560, margin: "0 auto" }}>
            The parts of the platform built on top of RACI, DMAIC, and readiness scoring, the pieces that make it a practitioner tool rather than a generic tracker.
          </p>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
            gap: 20,
          }}
        >
          {DIFFERENTIATORS.map((d) => (
            <div
              key={d.name}
              className="glass-card hover-card"
              style={{ padding: "1.5rem" }}
            >
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: "var(--radius)",
                  background: "var(--lilac-wash)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 14,
                }}
              >
                <i className={`ti ${d.icon}`} style={{ fontSize: 22, color: "var(--lilac-deep)" }} aria-hidden="true" />
              </div>
              <h3 style={{ fontSize: 15, fontWeight: 600, color: "var(--navy)", margin: "0 0 8px" }}>
                {d.name}
              </h3>
              <p style={{ fontSize: 13, color: "var(--text-secondary)", margin: 0, lineHeight: 1.6 }}>
                {d.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Comparison Grid Section */}
      <section>
        <div style={{ textAlign: "center", marginBottom: "2.5rem" }}>
          <p style={{ fontSize: 14, color: "var(--lilac-deep)", fontWeight: 600, margin: "0 0 10px" }}>
            Traditional tools help you track work. Groundwork helps you support judgement.
          </p>
          <h2 style={{ fontFamily: "var(--font-voice)", fontSize: 28, color: "var(--navy)", margin: "0 0 12px" }}>
            Groundwork TierUp vs. Tracking Alone
          </h2>
          <p style={{ fontSize: 15, color: "var(--text-secondary)", maxWidth: 540, margin: "0 auto" }}>
            Why delivery leads move beyond task trackers.
          </p>
        </div>

        <div
          style={{
            background: "var(--surface-0)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-lg)",
            overflow: "hidden",
            boxShadow: "var(--shadow-sm)",
          }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14, textAlign: "left" }}>
            <thead>
              <tr style={{ background: "var(--navy)", color: "#fff" }}>
                <th style={{ padding: "16px 20px", fontWeight: 600, width: "25%" }}>Feature Capability</th>
                <th style={{ padding: "16px 20px", fontWeight: 600, width: "40%", background: "var(--lilac-deep)" }}>
                  Groundwork TierUp
                </th>
                <th style={{ padding: "16px 20px", fontWeight: 600, width: "35%", background: "#334155" }}>
                  Without right-sizing
                </th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON.map((row, idx) => (
                <tr
                  key={row.feature}
                  style={{
                    borderBottom: idx < COMPARISON.length - 1 ? "1px solid var(--border)" : "none",
                    background: idx % 2 === 0 ? "var(--surface-0)" : "var(--surface-1)",
                  }}
                >
                  <td style={{ padding: "16px 20px", fontWeight: 600, color: "var(--navy)" }}>{row.feature}</td>
                  <td style={{ padding: "16px 20px", color: "var(--text-primary)", fontWeight: 500 }}>
                    <i className="ti ti-check" style={{ color: "var(--green)", marginRight: 8, fontWeight: "bold" }} aria-hidden="true" />
                    {row.groundwork}
                  </td>
                  <td style={{ padding: "16px 20px", color: "var(--text-secondary)" }}>
                    <i className="ti ti-x" style={{ color: "#EF4444", marginRight: 8 }} aria-hidden="true" />
                    {row.traditional}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* CTA Footer Section */}
      <section
        style={{
          marginTop: "4rem",
          background: "var(--lilac-wash)",
          borderRadius: "var(--radius-lg)",
          padding: "3rem 2rem",
          textAlign: "center",
        }}
      >
        <h3 style={{ fontFamily: "var(--font-voice)", fontSize: 24, color: "var(--navy)", margin: "0 0 12px" }}>
          Ready to eliminate decision relitigation on your project?
        </h3>
        <p style={{ fontSize: 14, color: "var(--text-secondary)", margin: "0 0 24px" }}>
          Built for senior project, change and BI leaders.
        </p>
        <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
          <Button href="https://groundworktierup.app/?intent=register" external>
            Get Started &rarr;
          </Button>
          <Button to="/calculator" variant="secondary">
            Calculate Delay Cost &rarr;
          </Button>
        </div>
      </section>
    </div>
  );
}
